import React, { useState, useEffect, useRef } from 'react';
import {
  Smartphone,
  Tablet,
  RotateCw,
  Wifi,
  WifiOff,
  HardDrive,
  X,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { offlineStorage, type OfflineMediaItem } from '../lib/offlineStorage';
import { useThemeLanguage } from '../context/ThemeLanguageContext';

export type DeviceType = 'iphone' | 'ipad';
export type Orientation = 'portrait' | 'landscape';

export interface DeviceSimulatorProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DeviceSimulator({ isOpen, onClose }: DeviceSimulatorProps) {
  const { language } = useThemeLanguage();
  const { isOffline, toggleSimulatedOffline } = useNetworkStatus();
  const [device, setDevice] = useState<DeviceType>('iphone');
  const [orientation, setOrientation] = useState<Orientation>('portrait');
  const [scale, setScale] = useState<number>(0.82);
  const [showStorageInspector, setShowStorageInspector] = useState(false);
  const [storageItems, setStorageItems] = useState<OfflineMediaItem[]>([]);
  const [storageUsage, setStorageUsage] = useState({ usedBytes: 0, formatted: '0 B', count: 0 });
  const [iframeKey, setIframeKey] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Update storage stats when inspector opens
  useEffect(() => {
    if (showStorageInspector) {
      offlineStorage.getOfflineItems().then(setStorageItems);
      offlineStorage.getStorageUsage().then(setStorageUsage);
    }
  }, [showStorageInspector]);

  // Adjust default scale based on window size
  useEffect(() => {
    const handleResize = () => {
      const h = window.innerHeight;
      const w = window.innerWidth;
      if (h < 750 || w < 900) {
        setScale(0.65);
      } else if (h < 900) {
        setScale(0.75);
      } else if (h < 1050) {
        setScale(0.82);
      } else {
        setScale(0.88);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keyboard shortcut Esc to close simulator
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Dimensions
  // iPhone 16 Pro: 393 x 852
  // iPad Pro 11": 834 x 1194
  const isIphone = device === 'iphone';
  const isPortrait = orientation === 'portrait';

  const screenWidth = isIphone
    ? isPortrait ? 393 : 852
    : isPortrait ? 834 : 1194;

  const screenHeight = isIphone
    ? isPortrait ? 852 : 393
    : isPortrait ? 1194 : 834;

  const toggleOrientation = () => {
    setOrientation((prev) => (prev === 'portrait' ? 'landscape' : 'portrait'));
  };

  const handleReloadIframe = () => {
    setIframeKey((prev) => prev + 1);
  };

  // Build target URL with sim_frame=1 parameter to avoid recursive simulator dock
  const iframeSrc = (() => {
    try {
      const u = new URL(window.location.href);
      u.searchParams.set('sim_frame', '1');
      return u.toString();
    } catch {
      return '/?sim_frame=1';
    }
  })();

  return (
    <div className="fixed inset-0 z-[1000] bg-[#09090b] flex flex-col select-none overflow-hidden font-sans animate-in fade-in duration-200">
      {/* ─── Simulator Top Toolbar ─── */}
      <header className="h-14 bg-[#141416] border-b border-white/10 px-4 flex items-center justify-between z-50 shrink-0 shadow-lg">
        {/* Left: Device & Studio Badge */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gradient-to-r from-accent-primary/20 to-purple-600/20 border border-white/10 text-white font-bold text-xs">
            <Sparkles className="w-3.5 h-3.5 text-accent-primary" />
            <span>Homeflix Mobile Studio</span>
          </div>

          {/* Device Switcher */}
          <div className="flex items-center p-0.5 rounded-xl bg-black/50 border border-white/10 text-xs">
            <button
              onClick={() => setDevice('iphone')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                isIphone ? 'bg-accent-primary text-white font-bold shadow' : 'text-neutral-400 hover:text-white'
              }`}
            >
              <Smartphone className="w-3.5 h-3.5" />
              <span>iPhone 16 Pro</span>
            </button>
            <button
              onClick={() => setDevice('ipad')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                !isIphone ? 'bg-accent-primary text-white font-bold shadow' : 'text-neutral-400 hover:text-white'
              }`}
            >
              <Tablet className="w-3.5 h-3.5" />
              <span>iPad Pro 11"</span>
            </button>
          </div>
        </div>

        {/* Center: Controls (Rotate, Reload, Offline Simulation, Zoom) */}
        <div className="flex items-center gap-2.5">
          {/* Rotate Orientation */}
          <button
            onClick={toggleOrientation}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-neutral-300 hover:text-white transition-all cursor-pointer"
            title={language === 'he' ? 'סובב מכשיר (לאורך / לרוחב)' : 'Rotate orientation'}
          >
            <RotateCw className="w-3.5 h-3.5 text-neutral-400" />
            <span>{isPortrait ? (language === 'he' ? 'לאורך' : 'Portrait') : (language === 'he' ? 'לרוחב' : 'Landscape')}</span>
          </button>

          {/* Reload Iframe */}
          <button
            onClick={handleReloadIframe}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-neutral-300 hover:text-white transition-all cursor-pointer"
            title={language === 'he' ? 'רענן מכשיר' : 'Reload frame'}
          >
            <RefreshCw className="w-3.5 h-3.5 text-neutral-400" />
          </button>

          {/* Offline Simulation Switch */}
          <button
            onClick={toggleSimulatedOffline}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-xl border text-xs font-bold transition-all shadow-sm cursor-pointer ${
              isOffline
                ? 'bg-amber-500/20 text-amber-300 border-amber-500/40 hover:bg-amber-500/30 ring-2 ring-amber-500/20'
                : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/25'
            }`}
            title={language === 'he' ? 'הדמה ניתוק מהאינטרנט לבדיקת אופליין' : 'Simulate offline network cut'}
          >
            {isOffline ? <WifiOff className="w-3.5 h-3.5 animate-pulse text-amber-400" /> : <Wifi className="w-3.5 h-3.5" />}
            <span>
              {isOffline
                ? language === 'he' ? 'מצב אופליין' : 'Offline Mode'
                : language === 'he' ? 'מחובר לרשת' : 'Online'}
            </span>
          </button>

          {/* Sandbox Storage Inspector */}
          <button
            onClick={() => setShowStorageInspector(!showStorageInspector)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-neutral-300 hover:text-white transition-all cursor-pointer"
            title={language === 'he' ? 'צפה באחסון הפנימי של האפליקציה' : 'Inspect Sandbox Storage'}
          >
            <HardDrive className="w-3.5 h-3.5 text-emerald-400" />
            <span>{storageUsage.formatted}</span>
          </button>

          {/* Scale Zoom Controls */}
          <div className="hidden lg:flex items-center gap-1 text-[11px] text-neutral-400 font-mono px-2 py-1 rounded-lg bg-black/50 border border-white/10">
            <button
              onClick={() => setScale(Math.max(0.45, Number((scale - 0.05).toFixed(2))))}
              className="px-1.5 hover:text-white cursor-pointer"
            >
              -
            </button>
            <span>{Math.round(scale * 100)}%</span>
            <button
              onClick={() => setScale(Math.min(1.05, Number((scale + 0.05).toFixed(2))))}
              className="px-1.5 hover:text-white cursor-pointer"
            >
              +
            </button>
          </div>
        </div>

        {/* Right: Exit Simulator Button */}
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-accent-primary hover:brightness-110 text-white text-xs font-bold transition-all shadow-md active:scale-95 cursor-pointer"
          >
            <X className="w-4 h-4" />
            <span>{language === 'he' ? 'סגור סימולטור (Esc)' : 'Exit Simulator (Esc)'}</span>
          </button>
        </div>
      </header>

      {/* ─── Simulator Workspace / Canvas ─── */}
      <div className="flex-1 overflow-auto flex items-center justify-center p-4 md:p-8 bg-[radial-gradient(#222_1px,transparent_1px)] [background-size:20px_20px] relative">
        {/* Device Outer Chassis with Hardware Accents */}
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
            transition: 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), width 0.3s ease, height 0.3s ease',
          }}
          className="relative flex-shrink-0"
        >
          {/* External Hardware Buttons (Volume & Power notches) */}
          {isIphone && isPortrait && (
            <>
              {/* Left Side: Action button & Volume Up/Down */}
              <div className="absolute -left-[5px] top-[115px] w-[5px] h-[28px] bg-[#3a3a3c] rounded-l-md border-l border-white/20" />
              <div className="absolute -left-[5px] top-[160px] w-[5px] h-[50px] bg-[#3a3a3c] rounded-l-md border-l border-white/20" />
              <div className="absolute -left-[5px] top-[225px] w-[5px] h-[50px] bg-[#3a3a3c] rounded-l-md border-l border-white/20" />
              {/* Right Side: Power Button */}
              <div className="absolute -right-[5px] top-[175px] w-[5px] h-[75px] bg-[#3a3a3c] rounded-r-md border-r border-white/20" />
            </>
          )}

          {/* Chassis Frame */}
          <div
            style={{
              width: `${screenWidth + (isIphone ? 26 : 32)}px`,
              height: `${screenHeight + (isIphone ? 26 : 32)}px`,
              borderRadius: isIphone ? 54 : 36,
            }}
            className="relative bg-[#1c1c1e] p-[13px] shadow-[0_30px_90px_rgba(0,0,0,0.95),0_0_0_2px_rgba(255,255,255,0.12),inset_0_0_0_2px_rgba(0,0,0,0.9)] border border-neutral-700/60 flex items-center justify-center overflow-hidden"
          >
            {/* Screen Bezel and Display Viewport */}
            <div
              style={{
                width: `${screenWidth}px`,
                height: `${screenHeight}px`,
                borderRadius: isIphone ? 42 : 24,
              }}
              className="relative overflow-hidden bg-black shadow-inner flex flex-col"
            >
              {/* iPhone Dynamic Island */}
              {isIphone && isPortrait && (
                <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-50 pointer-events-none flex items-center justify-center">
                  <div className="w-[122px] h-[32px] bg-black rounded-full shadow-[0_0_0_1.5px_rgba(255,255,255,0.08)] flex items-center justify-between px-3">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#111] border border-neutral-800" />
                    <div className="w-3 h-3 rounded-full bg-blue-950/70 border border-blue-800/40" />
                  </div>
                </div>
              )}

              {/* Native iOS Fake Status Bar */}
              <div className="h-8 w-full flex items-center justify-between px-6 text-[12px] font-semibold text-white/90 z-40 pointer-events-none select-none bg-gradient-to-b from-black/80 to-transparent shrink-0">
                <span className="font-mono text-xs">9:41</span>
                <div className="flex items-center gap-1.5">
                  {isOffline ? (
                    <WifiOff className="w-3.5 h-3.5 text-amber-400" />
                  ) : (
                    <Wifi className="w-3.5 h-3.5 text-white" />
                  )}
                  {/* Battery Icon */}
                  <div className="w-5 h-2.5 rounded-sm border border-white/80 p-0.5 flex items-center">
                    <div className="h-full w-full bg-emerald-400 rounded-2xs" />
                  </div>
                </div>
              </div>

              {/* The Actual Running Homeflix Web Application in isolated Mobile Viewport */}
              <div className="flex-1 w-full h-full relative overflow-hidden bg-[#0F0F0F]">
                <iframe
                  ref={iframeRef}
                  key={iframeKey}
                  src={iframeSrc}
                  title="Homeflix Mobile App Simulator"
                  className="w-full h-full border-0 bg-[#0F0F0F] select-auto"
                  style={{
                    width: `${screenWidth}px`,
                    height: `${screenHeight - 32 - 16}px`,
                  }}
                />
              </div>

              {/* Native iOS Home Bar */}
              <div className="h-4 w-full flex items-center justify-center pointer-events-none z-40 bg-gradient-to-t from-black/80 to-transparent shrink-0">
                <div className="w-32 h-1 bg-white/40 rounded-full" />
              </div>
            </div>
          </div>
        </div>

        {/* ─── Sandbox Storage Inspector Drawer ─── */}
        {showStorageInspector && (
          <div className="absolute top-4 right-4 bottom-4 w-96 bg-[#161618]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl z-50 flex flex-col animate-in slide-in-from-right-5 text-white">
            <div className="p-4 border-b border-white/10 flex items-center justify-between bg-[#1b1b1d] rounded-t-2xl">
              <div className="flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-emerald-400" />
                <h3 className="text-sm font-bold">
                  {language === 'he' ? 'מפקח אחסון פנימי (Sandbox)' : 'Internal Sandbox Inspector'}
                </h3>
              </div>
              <button
                onClick={() => setShowStorageInspector(false)}
                className="p-1 rounded-lg text-neutral-400 hover:text-white hover:bg-white/10 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 border-b border-white/5 bg-white/[0.02]">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-neutral-400">{language === 'he' ? 'נפח תפוס:' : 'Used Space:'}</span>
                <span className="font-bold text-emerald-400">{storageUsage.formatted}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-neutral-400">{language === 'he' ? 'קבצים שמורים:' : 'Saved Media:'}</span>
                <span className="font-bold text-white">{storageUsage.count}</span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {storageItems.length === 0 ? (
                <div className="py-16 text-center text-xs text-neutral-500">
                  {language === 'he' ? 'אין עדיין קבצים באחסון האופליין' : 'Sandbox storage is empty'}
                </div>
              ) : (
                storageItems.map((item) => (
                  <div key={item.id} className="p-3 rounded-xl bg-white/[0.04] border border-white/10 space-y-2">
                    <div className="flex items-center gap-2.5">
                      {item.posterDataUrl && (
                        <img src={item.posterDataUrl} alt={item.title} className="w-10 h-14 object-cover rounded-md flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <h5 className="text-xs font-bold text-white truncate">{item.title}</h5>
                        <div className="flex items-center gap-2 text-[10px] text-neutral-400 font-mono mt-0.5">
                          <span>{offlineStorage.formatBytes(item.sizeBytes)}</span>
                          {item.year && <span>({item.year})</span>}
                        </div>
                      </div>
                    </div>
                    {item.subtitles && item.subtitles.length > 0 && (
                      <div className="pt-2 border-t border-white/5 flex flex-wrap gap-1">
                        {item.subtitles.map((s) => (
                          <span key={s.streamIndex} className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 font-mono">
                            {s.language.toUpperCase()}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
